import React, { useState, useEffect, useCallback } from 'react';
import { Search, RotateCcw, AlertCircle, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { getWritingChecks, type GetWritingChecksParams, type WritingCheckListItem } from '@/modules/learning/api/writing';
import { useAuth } from '@/contexts/AuthContext';

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
const DEFAULT_PAGE_SIZE = 20;

const clampScore = (value: number | undefined) =>
  Math.max(0, Math.min(100, value ?? 0));

function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function truncate(text: string | undefined, maxLen: number): string {
  if (!text || typeof text !== 'string') return '—';
  const t = text.trim();
  if (t.length <= maxLen) return t;
  return t.slice(0, maxLen) + '…';
}

/** Reusable history content: filters + list. Use inside a page or modal. */
export type WritingChecksHistoryContentProps = {
  /** When true, fetch is skipped (e.g. modal not open). Omit or true when used on a page. */
  active?: boolean;
};

export const WritingChecksHistoryContent: React.FC<WritingChecksHistoryContentProps> = ({
  active = true,
}) => {
  const { hasRole } = useAuth();
  const showStudentFilter =
    hasRole('TEACHER') || hasRole('SCHOOL_ADMIN') || hasRole('DEV_TEAM');

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [assignmentId, setAssignmentId] = useState('');
  const [submissionId, setSubmissionId] = useState('');
  const [studentId, setStudentId] = useState('');

  const [items, setItems] = useState<WritingCheckListItem[]>([]);
  const [total, setTotal] = useState<number | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil((total ?? 0) / pageSize));

  const fetchChecks = useCallback(
    (params?: { page?: number; pageSize?: number } & Partial<GetWritingChecksParams>) => {
      if (!active) return;
      setLoading(true);
      setError(null);
      const page = params?.page ?? currentPage;
      const size = params?.pageSize ?? pageSize;
      const skip = (page - 1) * size;
      const query: GetWritingChecksParams = {
        limit: size,
        skip,
        from_date: params?.from_date ?? (fromDate.trim() || undefined),
        to_date: params?.to_date ?? (toDate.trim() || undefined),
        assignment_id: params?.assignment_id ?? (assignmentId.trim() || undefined),
        submission_id: params?.submission_id ?? (submissionId.trim() || undefined),
        student_id: params?.student_id ?? (studentId.trim() || undefined),
      };
      getWritingChecks(query)
        .then((res) => {
          setItems(res.items);
          setTotal(res.total);
          if (params?.page != null) setCurrentPage(params.page);
          if (params?.pageSize != null) setPageSize(params.pageSize);
        })
        .catch((err) => {
          setItems([]);
          setError(
            err?.message ?? 'Could not load history. Please try again.'
          );
        })
        .finally(() => setLoading(false));
    },
    [active, currentPage, pageSize, fromDate, toDate, assignmentId, submissionId, studentId]
  );

  useEffect(() => {
    if (!active) return;
    setLoading(true);
    setError(null);
    setCurrentPage(1);
    getWritingChecks({
      limit: DEFAULT_PAGE_SIZE,
      skip: 0,
    })
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
        setPageSize(DEFAULT_PAGE_SIZE);
      })
      .catch((err) => {
        setItems([]);
        setError(err?.message ?? 'Could not load history. Please try again.');
      })
      .finally(() => setLoading(false));
  }, [active]);

  const filterParams = () => ({
    from_date: fromDate.trim() || undefined,
    to_date: toDate.trim() || undefined,
    assignment_id: assignmentId.trim() || undefined,
    submission_id: submissionId.trim() || undefined,
    student_id: studentId.trim() || undefined,
  });

  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchChecks({ page: 1, pageSize, ...filterParams() });
  };

  const handleResetFilters = () => {
    setPageSize(DEFAULT_PAGE_SIZE);
    setCurrentPage(1);
    setFromDate('');
    setToDate('');
    setAssignmentId('');
    setSubmissionId('');
    setStudentId('');
    fetchChecks({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
  };

  const goToPage = (page: number) => {
    const p = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(p);
    fetchChecks({ page: p, pageSize, ...filterParams() });
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    fetchChecks({ page: 1, pageSize: size, ...filterParams() });
  };

  const inputClass =
    'w-full min-w-0 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm';
  const labelClass = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full gap-4">
      {/* Full-width filter bar: one horizontal row, wraps on smaller screens */}
      <div className="w-full shrink-0 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
        <div className="flex flex-wrap items-end gap-3 md:gap-4">
          <div className="w-full sm:w-auto min-w-[120px] flex-1 sm:flex-none">
            <label className={labelClass}>From date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className={inputClass}
            />
          </div>
          <div className="w-full sm:w-auto min-w-[120px] flex-1 sm:flex-none">
            <label className={labelClass}>To date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className={inputClass}
            />
          </div>
          {/* <div className="w-full sm:w-auto min-w-[140px] flex-1 sm:flex-none">
            <label className={labelClass}>Assignment ID</label>
            <input
              type="text"
              value={assignmentId}
              onChange={(e) => setAssignmentId(e.target.value)}
              placeholder="Optional"
              className={inputClass + ' placeholder-slate-400'}
            />
          </div> */}
          {/* <div className="w-full sm:w-auto min-w-[140px] flex-1 sm:flex-none">
            <label className={labelClass}>Submission ID</label>
            <input
              type="text"
              value={submissionId}
              onChange={(e) => setSubmissionId(e.target.value)}
              placeholder="Optional"
              className={inputClass + ' placeholder-slate-400'}
            />
          </div> */}
          <div className="w-full sm:w-auto min-w-[80px] flex-none">
            <label className={labelClass}>Limit</label>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className={inputClass}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          {showStudentFilter && (
            <div className="w-full sm:w-auto min-w-[140px] flex-1 sm:flex-none">
              <label className={labelClass}>Student ID</label>
              <input
                type="text"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                placeholder="Optional"
                className={inputClass + ' placeholder-slate-400'}
              />
            </div>
          )}
          <div className="flex flex-wrap gap-2 shrink-0">
            <button
              type="button"
              onClick={handleApplyFilters}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium"
            >
              <Search className="w-4 h-4" />
              Apply
            </button>
            <button
              type="button"
              onClick={handleResetFilters}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-medium"
            >
              <RotateCcw className="w-4 h-4" />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Full-width results area: flex-1, scrolls */}
      <div className="flex flex-col flex-1 min-h-0 w-full min-w-0 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        {loading && (
          <div className="flex flex-col items-center justify-center py-16 text-slate-500 dark:text-slate-400">
            <div className="rounded-full h-10 w-10 border-4 border-slate-200 dark:border-slate-700 border-t-indigo-600 dark:border-t-indigo-500 animate-spin mb-4" />
            <p className="text-sm font-semibold">Loading writing checks…</p>
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <AlertCircle className="w-12 h-12 text-amber-500 dark:text-amber-400 mb-3" />
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 text-center">
              {error}
            </p>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400 dark:text-slate-500">
            <FileText className="w-12 h-12 mb-3 opacity-60" />
            <p className="text-sm font-medium">No writing checks found</p>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <ul className="divide-y divide-slate-200 dark:divide-slate-700">
            {items.map((item, idx) => (
              <li key={item.check_id ?? idx} className="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <div className="flex flex-col gap-2 text-sm">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-slate-500 dark:text-slate-400">
                    <span>{formatDate(item.created_at)}</span>
                    {item.student_info?.display_name && (
                      <span>{item.student_info.display_name}</span>
                    )}
                    {item.mode && (
                      <span className="rounded-full bg-slate-200 dark:bg-slate-700 px-2 py-0.5">
                        {item.mode}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs items-baseline">
                    <span>
                      Suggestions:{' '}
                      <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                        {item.derived_metadata?.suggestion_count ??
                          (Array.isArray((item as any).result?.suggestions)
                            ? (item as any).result.suggestions.length
                            : 0)}
                      </span>
                    </span>
                    <span>
                      Overall:{' '}
                      <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                        {Math.round(clampScore(item.result?.rubric?.overallScore ?? item.derived_metadata?.overall_score))}/100
                      </span>
                    </span>
                    <span>
                      G:{' '}
                      <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                        {Math.round(clampScore(item.result?.rubric?.grammar ?? item.derived_metadata?.grammar_score))}
                      </span>
                    </span>
                    <span>
                      C:{' '}
                      <span className="text-sm font-semibold text-sky-600 dark:text-sky-400">
                        {Math.round(clampScore(item.result?.rubric?.clarity ?? item.derived_metadata?.clarity_score))}
                      </span>
                    </span>
                    <span>
                      V:{' '}
                      <span className="text-sm font-semibold text-violet-600 dark:text-violet-400">
                        {Math.round(clampScore(item.result?.rubric?.vocabulary ?? item.derived_metadata?.vocabulary_score))}
                      </span>
                    </span>
                  </div>
                  <p className="text-slate-800 dark:text-slate-200 tamil-text">
                    {truncate(item.original_text, 120)}
                  </p>
                  {(item.result?.rubric?.feedback ?? (item as any).result?.rubric?.teacherNote) && (
                    <p className="text-xs text-slate-600 dark:text-slate-400 border-l-2 border-indigo-400 pl-2">
                      {item.result?.rubric?.feedback ?? (item as any).result?.rubric?.teacherNote}
                    </p>
                  )}
                  {(item.assignment_id || item.submission_id || item.task_id) && (
                    <div className="text-xs text-slate-400 dark:text-slate-500">
                      {item.assignment_id && <span>Assignment: {item.assignment_id}</span>}
                      {item.submission_id && <span className="ml-3">Submission: {item.submission_id}</span>}
                      {item.task_id && <span className="ml-3">Task: {item.task_id}</span>}
                    </div>
                  )}
                </div>
              </li>
            ))}
            </ul>
          </div>
        )}
      </div>

      {!loading && total !== undefined && items.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shrink-0 pt-4 px-1">
          <span className="text-sm text-slate-500 dark:text-slate-400">
            Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, total ?? 0)} of {total} check(s)
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1 || loading}
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50 disabled:pointer-events-none text-slate-600 dark:text-slate-400"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300 min-w-[6rem] text-center">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages || loading}
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50 disabled:pointer-events-none text-slate-600 dark:text-slate-400"
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/** Legacy modal wrapper; prefer using WritingChecksHistoryContent on WritingHistoryPage. */
export type WritingChecksHistoryPanelProps = {
  open: boolean;
  onClose: () => void;
};

export const WritingChecksHistoryPanel: React.FC<WritingChecksHistoryPanelProps> = ({
  open,
  onClose,
}) => {
  if (!open) return null;
  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Writing check history"
      size="xl"
      showCloseButton
    >
      <WritingChecksHistoryContent active={true} />
    </Modal>
  );
};

export default WritingChecksHistoryPanel;
